import { useEffect, useRef } from 'react'

import { setOptimizelyExperiment, setOptimizelyFeatureFlag } from 'actions/OptimizelyActions'
import { optimizelyIsReady, userContext } from 'analytics/optimizely/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import createSelector from 'lib/web/createSelector'
import { reportClientError } from 'services/errorReportingService'
import config from 'constants/config'
import { setCookie } from 'lib/web/cookieUtils'
import moment from 'moment'

export function setOptimizelyTriggeredCookie(flagKey: OptimizelyExperiments) {
  const expirationTimestamp = moment().add(2, 'months').toDate().getTime()
  const currentDateTime = moment().format('YYYY-MM-DD:HH-mm')
  setCookie(`${flagKey}_triggered`, currentDateTime, expirationTimestamp, '/', 'Lax')
}

export enum OptimizelyExperiments {
  searchPriceMapMarkers = 'search_price_map_markers',
  searchTypeaheadOpenSearch = 'search_typeahead_opensearch_v2',
  SearchFijiExpansion = 'search_fiji_expansion',
  paymentsFlightsMerchantFees = 'payments_flight_merchant_fees',
  tripExplore = 'trip_explore',
  paymentsBookingProtectionInternational = 'payments_booking_protection',
  offerPageHotelAndFlightsToggle = 'cro_offer_page_hotel_and_flights_toggle',
  searchMapSplitView = 'search_map_split_view',
  searchBroadSearchLocation = 'search_broad_search_location',
  searchFlightDeal = 'search_flight_deal',
  cruiseSearchNewAlgorithm = 'cruise_search_new_algorithm',
  searchMobileSlimUI = 'search_mobile_slim_ui',
  cruiseNewBookingFlow = 'cruise_new_cruisebank_booking_flow_may_2024',
  flightPreFillRecentSearch = 'flights_pre_fill_recent_search',
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  searchFamilySegmentation = 'search_family_segmentation',
  paymentsHotelsDepositsAu = 'payments_hotels_deposits_au',
  paymentsHotelsDepositsNonAu = 'payments_hotels_deposits_non_au',
  lpcChangeDatesEnabled = 'opex_lpc_change_dates',
  nonFlashNextAlgo = 'search_non_flash_next_algo',
  pricePerNight = 'cro_price_per_night',
  lereGraphRecommendation = 'lere_graph_recommendation',
  lerePersonalisedTours = 'lere_personalised_tours',
  lereSearchModel = 'lere_search_model',
  bedbankChangeDatesEnabled = 'opex_bedbank_change_dates',
  searchClientCheckAvailability = 'search_client_check_availability',
  homepageOfferCardSizeMobile = 'cro_simplified_homepage_offer_card_mobile',
  homepageOfferCardSizeDesktop = 'cro_simplified_homepage_offer_card_desktop',
  searchClusterMapPins = 'search_cluster_map_pins',
  newMobileHotelOfferPage = 'cro_app_style_hotel_offer_page_booking_flow',
  croAppPromoAuEnabled = 'cro_app_promo_au',
  supportAssistantWidgetEnabled = 'opex_lesa_widget',
  searchCtrBasedSearchAlgorithm = 'search_ctr_based_search_algorithm',
  disabledInflowExperiences = 'cro_disabled_inflow_experiences',
  expediaStyleLoginPrompt = 'expedia_style_login_prompt',
  luxPlusCheckoutSavings = 'lux_plus_checkout_savings',
  emailChangeEnabled = 'opex_email_change',
  romanticLandingPage = 'romantic_properties_v1_thematic_landing_page_a_b_test',
}

export enum OptimizelyFeatureFlags {
  luxPlusFeatureToggle = 'luxplus_feature_toggle',
  luxPlusToursToggle = 'luxplus_tours_toggle',
  isGoCardlessEnabled = 'payments_go_cardless',
  allowPhoneNumberLogin = 'allow_phone_number_login',
  promoAppBanner = 'app_banner_targeted_delivery',
  CSAgentsAutoQuote = 'cro_cs_agents_auto_quote',
  tourOptionalExtras = 'tours_optional_extras',
  tourReviewsEnabled = 'cro_tour_reviews',
  travelProtection = 'cro_travel_protection'
}

/**
 * This is the only function that will put users into the report for your AB test.
 * We need to label our control group in Optimizely as 'control' as this means Optimizely will
 * return the 'enabled' value as true for both control and variant which we can use to determine if
 * a user has triggered the experiment report.  We then use our own 'enabled'
 * key to return true 'only' if a user is in the variant. This function should only be called by it's Action.
 */
export async function activateOptimizelyExperiment(flagKey: OptimizelyExperiments | OptimizelyFeatureFlags) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  const success = await optimizelyIsReady

  if (success) {
    try {
      const decision = userContext.decideForKeys([flagKey])[flagKey]
      const experimentValues = {
        variationKey: decision.variationKey,
        enabled: getOptimizelyEnabledDecision(decision.variationKey),
        triggered: !!decision.enabled,
        variables: decision.variables,
      }
      return experimentValues
    } catch (e) {
      reportClientError(e, { flagKey }) }
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.optimizely.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyExperiments | OptimizelyFeatureFlags, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experimentActivated = useRef(false)

  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      if (isFeatureFlagExperiment(experimentId)) {
        dispatch(setOptimizelyFeatureFlag(experimentId))
      } else {
        dispatch(setOptimizelyExperiment(experimentId))
      }
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  const experimentVariation = useAppSelector(state => {
    if (isFeatureFlagExperiment(experimentId)) {
      return getOptimizelyFeatureFlagVariation(state, experimentId)
    } else {
      return getOptimizelyExperimentVariation(state, experimentId)
    }
  })

  if (canTriggerExperiment) {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) {
      return config[`OPTIMIZELY_${experimentId}`]
    }

    return experimentVariation
  }
}

export function isFeatureFlagExperiment(experimentId: OptimizelyExperiments | OptimizelyFeatureFlags): experimentId is OptimizelyFeatureFlags {
  return typeof experimentId === 'string' && Object.values(OptimizelyFeatureFlags).includes(experimentId as OptimizelyFeatureFlags)
}

/**
 *
 *  Gets all Optimizely experiments and returns an object of triggered experiments required for Snowplow experiment context
 */
export function allOptimizelyExperiments(state: App.State) {
  const allExperiments = state.optimizely.optimizelyExperiments

  const triggeredExperiments = Object.entries(allExperiments)
    .filter(([_, decisions]) => decisions?.triggered && decisions?.variationKey)
    .map(([key, decisions]) => ({
      key: `exp_${key}`,
      value: decisions?.variationKey || '',
    }))

  return triggeredExperiments
}

export const getOptimizelyExperimentVariation = createSelector(
  (_: App.State, experimentId: OptimizelyExperiments) => experimentId,
  (state: App.State, experimentId: OptimizelyExperiments) => state.optimizely.optimizelyExperiments[experimentId],
  (experimentId, experiment): string | undefined => {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) { return config[`OPTIMIZELY_${experimentId}`] }
    if (!experiment?.enabled) {
      return
    }

    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }

    return experiment.variationKey
  },
)

export const getOptimizelyFeatureFlagVariation = createSelector(
  (state: App.State, experimentId: OptimizelyFeatureFlags) => state.optimizely.optimizelyFeatureFlags[experimentId],
  (experiment) => {
    return !!experiment
  },
)

export function getOptimizelyEnabledDecision(experimentVariationKey: string | null): boolean {
  return !!(experimentVariationKey && (experimentVariationKey !== 'off' && experimentVariationKey !== 'control'))
}
